import { Box, BoxSx, Tabs, Text } from "@modernatx-internal/component-library";
import React from "react";

import { useExperience } from "@/context/ExperienceContext";

import { Sticky, StickyProvider } from "./Sticky";

export type DisplayTableDensity = "comfortable" | "compact";

type ReactComponentNode<T> =
  | (React.ReactElement<T> | React.ReactElement<T>[] | boolean | null)[]
  | React.ReactElement<T>
  | null
  | boolean;

interface DisplayTableBodyProps {
  isLastBody?: boolean;
  children?: ReactComponentNode<DisplayTableRowProps>;
}

interface DisplayTableGroup {
  id: string;
  label: React.ReactNode;
}

export interface DisplayTableProps {
  children?: ReactComponentNode<
    DisplayTableRowProps | DisplayTableHeaderProps | DisplayTableBodyProps
  >;
  groups?: DisplayTableGroup[];
  /** Show group labels above the desktop column headers. */
  showGroupHeaders?: boolean;
  showGroupBorders?: boolean;
  rounded?: boolean;
  density?: DisplayTableDensity;
  /** On small screens, show one selectable group or all groups in separate tables. */
  mobileLayout?: "tabs" | "stacked";
}

interface DisplayTableMobileEdges {
  start: boolean;
  topStart: boolean;
  topEnd: boolean;
  bottomStart: boolean;
  bottomEnd: boolean;
}

const tableCornerRadius = (
  rounded: boolean,
  desktopEdge?: boolean,
  mobileEdge?: boolean
): "radius-3" | null | ["radius-3" | 0, null, "radius-3" | 0] => {
  const desktopRadius = rounded && desktopEdge ? "radius-3" : null;
  return mobileEdge === undefined
    ? desktopRadius
    : [rounded && mobileEdge ? "radius-3" : 0, null, desktopRadius ?? 0];
};

interface DisplayTableHeaderCellProps {
  rowSpan?: number;
  scope?: "col" | "colgroup" | "row" | "rowgroup";
  brand?: "primary" | "secondary" | "dark";
  children?: React.ReactNode;
  colSpan?: number;
  group?: string;
  groupNext?: string;
  groupPrev?: string;
  grow?: number;
  growTotal?: number;
  isEndEndCell?: boolean;
  isEndStartCell?: boolean;
  isStartCell?: boolean;
  isStartEndCell?: boolean;
  isStartStartCell?: boolean;
  mobileEdges?: DisplayTableMobileEdges;
  sx?: BoxSx;
  verticalAlign?: "bottom" | "middle" | "top";
}

interface DisplayTableHeaderProps {
  children?: ReactComponentNode<DisplayTableRowProps>;
}

interface DisplayTableCellProps {
  rowSpan?: number;
  children?: React.ReactNode;
  colSpan?: number;
  group?: string;
  groupNext?: string;
  groupPrev?: string;
  isEndEndCell?: boolean;
  isEndStartCell?: boolean;
  isStartCell?: boolean;
  isStartEndCell?: boolean;
  isStartStartCell?: boolean;
  mobileEdges?: DisplayTableMobileEdges;
  sx?: BoxSx;
}

interface DisplayTableRowProps {
  children?: ReactComponentNode<DisplayTableCellProps | DisplayTableHeaderCellProps>;
  cellColumns?: number[];
  columnCount?: number;
  cellEnds?: boolean[];
  isFirstRow?: boolean;
  isLastRow?: boolean;
  sx?: BoxSx;
}

interface DisplayTableGroupDefinition {
  groupId?: string;
  grow?: number;
}

export interface DisplayTableContextProps {
  density: DisplayTableDensity;
  groupActive?: string;
  groupDefinitions?: DisplayTableGroupDefinition[];
  groups?: DisplayTableProps["groups"];
  showGroupHeaders?: boolean;
  showGroupBorders?: boolean;
  hasHeader: boolean;
  rounded: boolean;
  totalGrow?: number;
  stickyHeader?: boolean;
}

export const DisplayTableContext = React.createContext<DisplayTableContextProps>({
  density: "comfortable",
  hasHeader: false,
  rounded: false
});

const DisplayTableBodyRowContext = React.createContext({ rowIndex: 0, rowCount: 1 });

function findElementsOfType<T extends React.ElementType>(
  children: React.ReactNode,
  targetType: T[],
  stopSearchingOnMatch?: boolean
): React.ReactElement<React.ComponentProps<T>>[] {
  const matches: React.ReactElement<React.ComponentProps<T>>[] = [];

  const search = (nodes: React.ReactNode): void => {
    React.Children.toArray(nodes).forEach((child) => {
      let matched = false;

      if (!React.isValidElement(child)) {
        return;
      }

      if (targetType.some((t) => t === child.type)) {
        matched = true;
        matches.push(child as React.ReactElement<React.ComponentProps<T>>);
      }

      if (child.props?.children && !(matched && stopSearchingOnMatch)) {
        search(child.props.children);
      }
    });
  };

  search(children);

  return matches;
}

function positionRows(rows: React.ReactElement<DisplayTableRowProps>[]) {
  const occupiedUntil: number[] = [];
  const positioned = rows.map((row, rowIndex) => {
    const cells = findElementsOfType(row.props.children, [
      DisplayTableCell,
      DisplayTableHeaderCell,
      DisplayTableRowHeaderCell
    ]);
    let column = 0;
    const cellEnds: boolean[] = [];
    const cellColumns = cells.map((cell) => {
      while ((occupiedUntil[column] ?? 0) > rowIndex) {
        column += 1;
      }
      const start = column;
      const endRow = cell.props.rowSpan === 0 ? rows.length : rowIndex + (cell.props.rowSpan || 1);
      for (let span = 0; span < (cell.props.colSpan || 1); span += 1) {
        occupiedUntil[column] = endRow;
        column += 1;
      }
      cellEnds.push(endRow >= rows.length);
      return start;
    });
    return React.cloneElement(row, { cellColumns, cellEnds });
  });
  return positioned.map((row) => React.cloneElement(row, { columnCount: occupiedUntil.length }));
}

const DisplayTable: React.FC<DisplayTableProps> = ({
  children,
  density = "comfortable",
  groups,
  showGroupHeaders = true,
  showGroupBorders = true,
  mobileLayout = "tabs",
  rounded = false
}) => {
  const { expandedMode } = useExperience();
  const [groupActive, groupActiveSet] = React.useState<string | undefined>(groups?.[0]?.id);
  const tableHeader = findElementsOfType(children, [DisplayTableHeader], true);
  const tableBodies = findElementsOfType(children, [DisplayTableBody], true);
  const tableRow = findElementsOfType(children, [DisplayTableRow], true)?.[0];
  const tableHeaderCells = findElementsOfType(tableRow?.props.children, [DisplayTableHeaderCell]);
  const columnGrowValues = tableHeaderCells.map(
    (tableCell) => tableCell.props.grow ?? tableCell.props.colSpan ?? 1
  );
  const totalGrow = columnGrowValues.reduce((acc, val) => acc + val, 0) || 1;
  const hasGroups = !!(groups && groups.length > 1);

  const groupDefinitions = tableHeaderCells.reduce(
    (acc: DisplayTableGroupDefinition[], tableCell) => {
      const colSpan = tableCell.props.colSpan || 1;
      const groupId = tableCell.props.group;
      const groupDefinition = new Array(colSpan)
        .fill(null)
        .map(() => ({ groupId, grow: (tableCell.props.grow ?? colSpan) / colSpan }));
      acc.push(...groupDefinition);
      return acc;
    },
    []
  );

  const prepareSections = (nodes: React.ReactNode, bodyIndex = { current: 0 }): React.ReactNode =>
    React.Children.map(nodes, (child) => {
      if (!React.isValidElement<{ children?: React.ReactNode }>(child)) {
        return child;
      }
      if (child.type === DisplayTableBody) {
        bodyIndex.current += 1;
        return React.cloneElement(child as React.ReactElement<DisplayTableBodyProps>, {
          isLastBody: bodyIndex.current === tableBodies.length
        });
      }
      return child.props.children
        ? React.cloneElement(child, {}, prepareSections(child.props.children, bodyIndex))
        : child;
    });

  const renderTable = () => (
    <StickyProvider>
      <Box
        as="table"
        sx={{
          borderCollapse: "separate",
          minWidth: "100%",
          mt: groups ? 5 : 0,
          tableLayout: "fixed"
        }}
      >
        {prepareSections(children)}
      </Box>
    </StickyProvider>
  );

  if ((expandedMode || mobileLayout === "stacked") && hasGroups) {
    return (
      <>
        <Box
          sx={{
            display: ["flex", null, "none"],
            flexDirection: "column",
            gap: 5
          }}
        >
          {groups.map((group) => (
            <Box key={group.id}>
              <StickyProvider>
                <DisplayTableContext.Provider
                  value={{
                    density,
                    stickyHeader: mobileLayout !== "stacked",
                    groupActive: group.id,
                    groupDefinitions,
                    groups,
                    showGroupHeaders,
                    showGroupBorders,
                    hasHeader: !!tableHeader.length,
                    rounded,
                    totalGrow
                  }}
                >
                  {mobileLayout === "tabs" && (
                    <Sticky sx={{ backgroundColor: "surface-neutral" }}>
                      <Tabs items={groups} selectedTabId={group.id} onClickTab={groupActiveSet} />
                    </Sticky>
                  )}
                  {renderTable()}
                </DisplayTableContext.Provider>
              </StickyProvider>
            </Box>
          ))}
        </Box>
        <Box sx={{ display: ["none", null, "block"] }}>
          <StickyProvider>
            <DisplayTableContext.Provider
              value={{
                density,
                groupActive,
                groupDefinitions,
                groups,
                showGroupHeaders,
                showGroupBorders,
                hasHeader: !!tableHeader.length,
                rounded,
                totalGrow
              }}
            >
              <Sticky sx={{ backgroundColor: "surface-neutral" }}>
                <Box sx={{ display: ["block", null, "none"] }}>
                  <Tabs items={groups} selectedTabId={groupActive} onClickTab={groupActiveSet} />
                </Box>
              </Sticky>
              {renderTable()}
            </DisplayTableContext.Provider>
          </StickyProvider>
        </Box>
      </>
    );
  }

  return (
    <StickyProvider>
      <DisplayTableContext.Provider
        value={{
          density,
          groupActive,
          groupDefinitions,
          groups,
          showGroupHeaders,
          showGroupBorders,
          hasHeader: !!tableHeader.length,
          rounded,
          totalGrow
        }}
      >
        {hasGroups && (
          <Sticky sx={{ backgroundColor: "surface-neutral" }}>
            <Box sx={{ display: ["block", null, "none"] }}>
              <Tabs items={groups} selectedTabId={groupActive} onClickTab={groupActiveSet} />
            </Box>
          </Sticky>
        )}
        {renderTable()}
      </DisplayTableContext.Provider>
    </StickyProvider>
  );
};

const DisplayTableHeader: React.FC<DisplayTableHeaderProps> = ({ children }) => {
  const {
    groups,
    groupDefinitions,
    showGroupHeaders = true,
    stickyHeader
  } = React.useContext(DisplayTableContext);
  const Header = stickyHeader === false ? Box : Sticky;
  const tableRows = positionRows(findElementsOfType(children, [DisplayTableRow]));

  const groupDefinitionsReduced = groupDefinitions?.reduce<
    { colSpan: number; group?: DisplayTableGroup }[]
  >((acc, groupDefinition) => {
    const previous = acc[acc.length - 1];

    if (previous && previous.group?.id === groupDefinition.groupId) {
      previous.colSpan = previous.colSpan + 1;
    } else {
      const group = groups?.find((g) => g.id === groupDefinition.groupId);

      if (group) {
        return acc.concat({ colSpan: 1, group });
      } else {
        return acc.concat({ colSpan: 1 });
      }
    }

    return acc;
  }, []);

  const renderTableRows = () => {
    return tableRows.map((tableRow, i) => {
      return React.cloneElement(tableRow, {
        ...tableRow.props,
        isFirstRow: i === 0,
        isLastRow: false,
        cellEnds: [],
        key: i
      });
    });
  };

  return (
    <Header
      as="thead"
      sx={{
        backgroundColor: "surface-neutral",
        width: "100%"
      }}
      {...(stickyHeader === false ? {} : { markerAs: "thead" as const })}
    >
      {showGroupHeaders && groups && tableRows.length === 1 && (
        <DisplayTableRow sx={{ display: groups.length > 1 ? ["none", null, "revert"] : null }}>
          {groupDefinitionsReduced?.map((groupDefinition, i) => (
            <Box
              key={i}
              as={groupDefinition.group ? "th" : "td"}
              scope={groupDefinition.group ? "colgroup" : undefined}
              colSpan={groupDefinition.colSpan}
              sx={{
                px: groups.length === 1 ? 0 : 5,
                py: 5
              }}
            >
              {groupDefinition.group && (
                <Text
                  as="div"
                  sx={{
                    backgroundColor: "color-system-gray-100",
                    borderRadius: "radius-round",
                    px: 5,
                    py: 2,
                    textAlign: "center"
                  }}
                  variant="body-1-bold"
                >
                  {groupDefinition.group?.label}
                </Text>
              )}
            </Box>
          ))}
        </DisplayTableRow>
      )}
      {renderTableRows()}
    </Header>
  );
};

const DisplayTableHeaderCell: React.FC<DisplayTableHeaderCellProps> = ({
  brand,
  rowSpan,
  scope,
  children,
  colSpan,
  group,
  groupNext,
  groupPrev,
  grow,
  growTotal,
  isEndEndCell,
  isEndStartCell,
  isStartCell,
  isStartEndCell,
  isStartStartCell,
  mobileEdges,
  sx,
  verticalAlign
}) => {
  const {
    density,
    groupActive,
    rounded,
    showGroupBorders = true,
    totalGrow = 1
  } = React.useContext(DisplayTableContext);

  const backgroundColor = React.useMemo<BoxSx["backgroundColor"]>(() => {
    switch (brand) {
      case "primary":
        return "color-current-brand-700";
      case "secondary":
        return "color-current-brand-secondary-700";
      case "dark":
        return "color-current-brand-800";
      default:
        return "surface-neutral";
    }
  }, [brand]);

  if (grow !== undefined && grow < 0) {
    throw new Error("grow property must be >= 0.");
  }

  const fraction = (grow ?? 1) / (growTotal || totalGrow);

  const isEmpty =
    children === null ||
    children === undefined ||
    (Array.isArray(children) && children.length === 0);

  const borderLeft = React.useMemo(() => {
    if (showGroupBorders && group !== undefined && groupPrev !== undefined && group !== groupPrev) {
      return "2px solid white";
    }

    return null;
  }, [showGroupBorders, group, groupPrev]);

  const borderRight = React.useMemo(() => {
    if (showGroupBorders && groupNext !== undefined && group !== groupNext) {
      return "2px solid white";
    }

    return null;
  }, [showGroupBorders, group, groupNext]);

  const outsideBorderColor = brand ? "stroke-brand-emphasis" : "stroke-neutral-tertiary";
  const desktopBorderLeftColor = borderLeft
    ? "white"
    : rounded && isStartCell
      ? outsideBorderColor
      : null;

  return (
    <Box
      as="th"
      aria-hidden={isEmpty ? "true" : undefined}
      colSpan={colSpan}
      rowSpan={rowSpan}
      scope={scope}
      sx={{
        backgroundColor: backgroundColor,
        borderBottom: "1px solid",
        borderBottomColor: "stroke-brand-emphasis",
        borderEndEndRadius: tableCornerRadius(rounded, isEndEndCell, mobileEdges?.bottomEnd),
        borderEndStartRadius: tableCornerRadius(rounded, isEndStartCell, mobileEdges?.bottomStart),
        borderLeft: mobileEdges
          ? [
              rounded && mobileEdges.start ? "1px solid" : "none",
              null,
              borderLeft || (rounded && isStartCell ? "1px solid" : "none")
            ]
          : borderLeft || (rounded && isStartCell ? "1px solid" : null),
        borderLeftColor: mobileEdges
          ? [outsideBorderColor, null, desktopBorderLeftColor]
          : desktopBorderLeftColor,
        borderRight: ["none", null, borderRight],
        borderStartEndRadius: tableCornerRadius(rounded, isStartEndCell, mobileEdges?.topEnd),
        borderStartStartRadius: tableCornerRadius(rounded, isStartStartCell, mobileEdges?.topStart),
        color: brand ? "white" : null,
        display: [group && groupActive !== group ? "none" : "revert", null, "revert"],
        px: density === "compact" ? "spacing-3" : [2, null, 6],
        py: density === "compact" ? "spacing-3" : [3, null, 6],
        textAlign: "left",
        verticalAlign: verticalAlign ? verticalAlign : brand ? "middle" : "bottom",
        width: () => `${fraction * 100}%`,
        "&:not(:first-of-type)": {
          ...(!brand && {
            borders: "1px solid",
            borderLeftColor: "stroke-neutral-tertiary"
          }),
          textAlign: "center"
        },
        ...sx
      }}
    >
      <Text
        as="div"
        sx={{ fontWeight: "font-weight-bold" }}
        variant={density === "compact" ? "body-2-bold" : ["body-1-medium", null, "title-1-medium"]}
      >
        {children}
      </Text>
    </Box>
  );
};

const DisplayTableBody: React.FC<DisplayTableBodyProps> = ({ children, isLastBody = true }) => {
  const { hasHeader } = React.useContext(DisplayTableContext);
  const tableRows = positionRows(findElementsOfType(children, [DisplayTableRow]));

  const renderTableRows = () => {
    return tableRows.map((tableRow, i) => {
      return (
        <DisplayTableBodyRowContext.Provider
          key={i}
          value={{ rowIndex: i, rowCount: tableRows.length }}
        >
          {React.cloneElement(tableRow, {
            ...tableRow.props,
            isFirstRow: !hasHeader && i === 0,
            isLastRow: isLastBody && i === tableRows.length - 1,
            cellEnds: isLastBody ? tableRow.props.cellEnds : []
          })}
        </DisplayTableBodyRowContext.Provider>
      );
    });
  };

  return (
    <Box
      as="tbody"
      sx={{
        position: "relative",
        top: () => "-1px",
        "tr:not([data-selected='true']):nth-of-type(even)": {
          backgroundColor: "surface-neutral-secondary"
        },
        "tr:not([data-selected='true']):nth-of-type(odd)": {
          backgroundColor: "surface-neutral"
        }
      }}
    >
      {renderTableRows()}
    </Box>
  );
};

const DisplayTableRow: React.FC<DisplayTableRowProps> = ({
  children,
  isFirstRow,
  isLastRow,
  cellColumns,
  columnCount,
  cellEnds,
  sx
}) => {
  const { groupDefinitions, groupActive, groups } = React.useContext(DisplayTableContext);
  const tableCells = findElementsOfType(children, [
    DisplayTableCell,
    DisplayTableHeaderCell,
    DisplayTableRowHeaderCell
  ]);

  const renderTableCells = () => {
    let column = 0;
    let groupDefinitionPrevious: string | undefined;
    let mobileColumn = 0;
    const visibleCellIndices = tableCells.flatMap((cell, i) => {
      const start = cellColumns?.[i] ?? mobileColumn;
      mobileColumn = start + (cell.props.colSpan || 1);
      const group = cell.props.group ?? groupDefinitions?.[start]?.groupId;
      return group === undefined || group === groupActive ? [i] : [];
    });
    const firstVisibleCell = visibleCellIndices[0];
    const lastVisibleCell = visibleCellIndices[visibleCellIndices.length - 1];

    return tableCells.map((headerCell, i) => {
      const start = cellColumns?.[i] ?? column;
      column = start + (headerCell.props.colSpan || 1);
      const groupDefinition = groupDefinitions?.slice(start, column) ?? [];
      const groupNext = groupDefinitions?.[column]?.groupId;

      const element = React.cloneElement(headerCell, {
        ...headerCell.props,
        group: headerCell.props.group ?? groupDefinition?.[0]?.groupId,
        ...(headerCell.type === DisplayTableHeaderCell && {
          grow:
            (headerCell.props as DisplayTableHeaderCellProps).grow ??
            (groupDefinition.reduce((sum, definition) => sum + (definition.grow ?? 1), 0) || 1)
        }),
        groupNext,
        groupPrev: groupDefinitionPrevious,
        isEndEndCell:
          (isLastRow || cellEnds?.[i]) &&
          (columnCount ? column === columnCount : i === tableCells.length - 1),
        isEndStartCell: (isLastRow || cellEnds?.[i]) && start === 0,
        isStartCell: start === 0,
        isStartEndCell: isFirstRow && i === tableCells.length - 1,
        isStartStartCell: isFirstRow && i === 0,
        mobileEdges:
          groups && groups.length > 1
            ? {
                start: i === firstVisibleCell,
                topStart: !!isFirstRow && i === firstVisibleCell,
                topEnd: !!isFirstRow && i === lastVisibleCell,
                bottomStart: !!(isLastRow || cellEnds?.[i]) && i === firstVisibleCell,
                bottomEnd: !!(isLastRow || cellEnds?.[i]) && i === lastVisibleCell
              }
            : undefined,
        key: i
      });

      groupDefinitionPrevious = groupDefinition[0]?.groupId;

      return element;
    });
  };

  return (
    <Box as="tr" sx={sx}>
      {tableCells.length ? renderTableCells() : children}
    </Box>
  );
};

const DisplayTableBodyCell: React.FC<
  DisplayTableCellProps & { rowHeaderScope?: "row" | "rowgroup" }
> = ({
  rowHeaderScope,
  rowSpan,
  children,
  colSpan,
  group,
  isEndEndCell,
  isEndStartCell,
  isStartCell,
  isStartEndCell,
  isStartStartCell,
  mobileEdges,
  sx
}) => {
  const { density, groupActive, rounded } = React.useContext(DisplayTableContext);

  return (
    <Box
      as={rowHeaderScope ? "th" : "td"}
      colSpan={colSpan}
      rowSpan={rowSpan}
      scope={rowHeaderScope}
      sx={{
        borderBottom: "1px solid",
        borderColor: "stroke-neutral-secondary",
        borderEndEndRadius: tableCornerRadius(rounded, isEndEndCell, mobileEdges?.bottomEnd),
        borderEndStartRadius: tableCornerRadius(rounded, isEndStartCell, mobileEdges?.bottomStart),
        borderLeft: mobileEdges
          ? [
              rounded && mobileEdges.start ? "1px solid" : "none",
              null,
              rounded && isStartCell ? "1px solid" : "none"
            ]
          : rounded && isStartCell
            ? "1px solid"
            : null,
        // Reapply the color at the same breakpoint as the border shorthand.
        borderLeftColor: mobileEdges
          ? ["stroke-neutral-secondary", null, "stroke-neutral-secondary"]
          : rounded && isStartCell
            ? "stroke-neutral-secondary"
            : null,
        borderRight: "1px solid",
        borderRightColor: "stroke-neutral-secondary",
        borderStartEndRadius: tableCornerRadius(rounded, isStartEndCell, mobileEdges?.topEnd),
        borderStartStartRadius: tableCornerRadius(rounded, isStartStartCell, mobileEdges?.topStart),
        display: [group && groupActive !== group ? "none" : "revert", null, "revert"],
        px: density === "compact" ? "spacing-3" : [2, null, 6],
        py: density === "compact" ? "spacing-3" : [3, null, 6],
        textAlign: "left",
        verticalAlign: "middle",
        "&:not(:first-of-type)": {
          textAlign: "center"
        },
        ...sx
      }}
    >
      <Text
        as="div"
        sx={{ fontWeight: "font-weight-medium" }}
        variant={
          density === "compact" ? "body-2-medium" : ["body-1-medium", null, "title-1-medium"]
        }
      >
        {children}
      </Text>
    </Box>
  );
};

const DisplayTableCell: React.FC<DisplayTableCellProps> = (props) => (
  <DisplayTableBodyCell {...props} />
);

const DisplayTableRowHeaderCell: React.FC<DisplayTableCellProps> = (props) => {
  const { rowIndex, rowCount } = React.useContext(DisplayTableBodyRowContext);
  const spansBody =
    rowIndex === 0 && rowCount > 1 && (props.rowSpan === 0 || (props.rowSpan ?? 1) >= rowCount);

  return <DisplayTableBodyCell {...props} rowHeaderScope={spansBody ? "rowgroup" : "row"} />;
};

export {
  DisplayTable,
  DisplayTableBody,
  DisplayTableCell,
  DisplayTableHeader,
  DisplayTableHeaderCell,
  DisplayTableRow,
  DisplayTableRowHeaderCell
};
